import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface UserSettings {
  id: string;
  userId: string;
  emailTemplate: string;
  emailSubjectTemplate: string | null;
  emailIntroMessage: string | null;
  emailOutroMessage: string | null;
  agentSignatureName: string | null;
  agentSignatureEmail: string | null;
  agentSignaturePhone: string | null;
  agentSignatureCompany: string | null;
  templateCustomizedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UpdateUserSettings {
  emailSubjectTemplate?: string | null;
  emailIntroMessage?: string | null;
  emailOutroMessage?: string | null;
  agentSignatureName?: string | null;
  agentSignatureEmail?: string | null;
  agentSignaturePhone?: string | null;
  agentSignatureCompany?: string | null;
}

interface PreviewTemplate {
  emailSubjectTemplate?: string | null;
  emailIntroMessage?: string | null;
  emailOutroMessage?: string | null;
  agentSignatureName?: string | null;
  agentSignatureEmail?: string | null;
  agentSignaturePhone?: string | null;
  agentSignatureCompany?: string | null;
}

interface PreviewResponse {
  subject: string;
  html: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  if (response.status === 204) {
    return null as T;
  }
  return response.json();
}

export function useUserSettings() {
  return useQuery({
    queryKey: ['userSettings'],
    queryFn: () => fetchJson<UserSettings | null>(`${API_BASE}/api/user-settings`),
  });
}

export function useUpdateUserSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserSettings) =>
      fetchJson<UserSettings>(`${API_BASE}/api/user-settings`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
    },
  });
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (data: PreviewTemplate) =>
      fetchJson<PreviewResponse>(`${API_BASE}/api/user-settings/preview`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}
